import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import type Handsontable from 'handsontable/base';
import type { GridSettings } from 'handsontable/settings';

// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableEditorSessionUtils';
import { addClassToHotTable, removeClassFromHotTable } from '../utils/domUtils/hotTableDomMutationUtils';
import {
    fixSelectionBorderDisplay,
    repositionAutofillHandle,
} from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import useRefCallback from '../../../utils/react/useRefCallback';
import { TABLE_GRID_EDITOR_KEY, TABLE_TITLE_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { finishEditingElement, startEditingElement } from '../../selection/selectionActions';
import { updateCellSelectionMeta } from '../tableActions';
import { CellSelection } from '../../../../common/table/TableTypes';

type HotTableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

export type UseTableSelectionHandlersReturn = {
    afterSelection: HotTableHook<'afterSelection'>;
    afterSelectionEnd: HotTableHook<'afterSelectionEnd'>;
    afterDeselect: HotTableHook<'afterDeselect'>;
};

/**
 * Custom hook for handling table cell selection operations.
 */
export const useTableSelectionHandlers = ({
    elementId,
    currentlyEditingEditorId,
    currentEditorKey,
    hotTableInstanceRef,
    hotTableContainerRef,
}: {
    elementId: string;
    currentlyEditingEditorId?: string | null;
    currentEditorKey?: string | null;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
}): UseTableSelectionHandlersReturn => {
    const dispatch = useDispatch();

    const dispatchStartEditingTableGrid = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_GRID_EDITOR_KEY,
                    editorId: getTableGridEditorId(elementId),
                    elementId: undefined,
                }),
            ),
        [dispatch, elementId],
    );

    const dispatchSetCellSelection = useCallback(
        (cellSelection: CellSelection | null, selectionLayerLevel = 0) =>
            dispatch(updateCellSelectionMeta(elementId, cellSelection, selectionLayerLevel)),
        [dispatch, elementId],
    );

    const dispatchFinishEditingElement = useCallback(
        () => dispatch(finishEditingElement(elementId)),
        [dispatch, elementId],
    );

    const afterSelection = useRefCallback<UseTableSelectionHandlersReturn['afterSelection']>(
        (row, column, row2, column2, _preventScrolling, selectionLayerLevel) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance) return;

            requestAnimationFrame(() => {
                // Applied using plain javascript to improve performance, to avoid triggering another render
                addClassToHotTable(hotInstance, 'is-selecting-cell');
            });

            fixSelectionBorderDisplay(hotInstance);

            const isEditingCellOrGrid =
                !currentlyEditingEditorId ||
                currentlyEditingEditorId === getTableGridEditorId(elementId) ||
                currentlyEditingEditorId === getTableCellEditorId(elementId);

            if (!isEditingCellOrGrid) {
                requestAnimationFrame(() => dispatchFinishEditingElement());
            }

            // Apply new cell selection to redux state if it has changed
            const newCellSelection: CellSelection = [row, column, row2, column2];

            repositionAutofillHandle(hotInstance, hotTableContainerRef);

            dispatchSetCellSelection(newCellSelection, selectionLayerLevel);
        },
        [
            currentlyEditingEditorId,
            dispatchFinishEditingElement,
            dispatchSetCellSelection,
            elementId,
            hotTableContainerRef,
            hotTableInstanceRef,
        ],
    );

    const afterSelectionEnd = useRefCallback<UseTableSelectionHandlersReturn['afterSelectionEnd']>(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance) return;

        requestAnimationFrame(() => {
            // Applied using plain javascript to improve performance, to avoid triggering another render
            removeClassFromHotTable(hotInstance, 'is-selecting-cell');
        });

        if (currentlyEditingEditorId !== getTableGridEditorId(elementId)) {
            dispatchStartEditingTableGrid();
        }
    }, [currentlyEditingEditorId, dispatchStartEditingTableGrid, elementId, hotTableInstanceRef]);

    const afterDeselect = useRefCallback<UseTableSelectionHandlersReturn['afterDeselect']>(() => {
        // Use setTimeout here to make sure all operations have run before we check if
        // there are still selections on the table.
        setTimeout(() => {
            if (hotTableInstanceRef.current?.getSelected()) return;

            // Don't deselect / finish editing if the deselection occurred because the table title editor is focused
            if (currentEditorKey !== TABLE_TITLE_EDITOR_KEY) dispatchFinishEditingElement();

            dispatchSetCellSelection(null);
        });
    }, [currentEditorKey, dispatchFinishEditingElement, dispatchSetCellSelection, hotTableInstanceRef]);

    return {
        afterSelection,
        afterSelectionEnd,
        afterDeselect,
    };
};

export default useTableSelectionHandlers;
